import type {
  FlowplayerUMD,
  Player,
  Plugin as FlowplayerPlugin,
} from "@flowplayer/player";
import { onDomReady } from "../../utils/on-dom-ready";

export type PlaylistReadyCallback = (
  globalFlowplayer: ExtendedFlowplayerUMD,
  localFlowplayer: ExtendedPlayer
) => void;

/**
 * The callback will be called on each flow player instance,
 * after the play list information was loaded.
 */
export const onFlowplayerPlaylistReady = (callback: PlaylistReadyCallback) => {
  // The individual phases are split into multiple functions:
  // 1. waitForFlowplayerInstancesAndContinue
  //    - waits for the (async) flow player loader script
  // 2. tryUsingAlreadyAvailableInstanceAndContinue
  //    - waits for the loading phase of flow player plugins
  // 3. afterFlowplayerInstancesObtained
  //    - waits for the initialization of the flow player plugin
  // 4. afterFlowplayerPlaylistPluginInitialization
  //    - waits for the playlist information to be loaded
  waitForFlowplayerInstancesAndContinue(callback);
};

const waitForFlowplayerInstancesAndContinue = (
  callback: PlaylistReadyCallback
) => {
  // Check if we're the one being late and handle it if we are.
  if (tryUsingAlreadyAvailableInstanceAndContinue(callback)) {
    return;
  }

  // The flow player entry-point was not yet executed.
  // Wait for its execution before registering the custom plugin.
  onDomReady(() => {
    // Some time passed ...
    // Check if we're late now and handle it if we are.
    if (tryUsingAlreadyAvailableInstanceAndContinue(callback)) {
      return;
    }

    const flowPlayerLoadScriptElement =
      document.querySelector<HTMLScriptElement>("script.js-flowplayer-loader");

    if (!flowPlayerLoadScriptElement) {
      // eslint-disable-next-line no-console
      console.error("No flow player loader script found on page");
      return;
    }

    flowPlayerLoadScriptElement.addEventListener(
      "load",
      () => {
        if (!tryUsingAlreadyAvailableInstanceAndContinue(callback)) {
          // eslint-disable-next-line no-console
          console.error(
            "Failed to initialize flow player. The global flowplayer property isn't available."
          );
        }
      },
      { once: true }
    );
  });
};

/**
 * Use the global flow player instance to continue or return false,
 * if it isn't possible yet.
 */
const tryUsingAlreadyAvailableInstanceAndContinue = (
  callback: PlaylistReadyCallback
) => {
  const globalFlowplayer = window.flowplayer;

  // The flow player initialization is complete.
  // If we'd add a plugin now, it wouldn't be initialized.
  if (globalFlowplayer && globalFlowplayer.version) {
    for (const playerElement of document.querySelectorAll<HTMLElement>(
      ".js-custom-flowplayer-playlist-ui"
    )) {
      const localFlowplayer = globalFlowplayer(playerElement);
      afterFlowplayerInstancesObtained(
        globalFlowplayer,
        localFlowplayer,
        callback
      );
    }
    return true;
  }

  // The flow player entry-point was already executed and
  // is in the loading phase. Registering a custom flow player
  // plugin in order to get notified when the loading completes.
  if (globalFlowplayer) {
    class FlowplayerTestExtension implements FlowplayerPlugin {
      public constructor(umd: FlowplayerUMD, player: Player) {
        afterFlowplayerInstancesObtained(umd, player, callback);
      }

      // eslint-disable-next-line class-methods-use-this
      public init() {
        // noop to conform to the Plugin interface
      }
    }

    globalFlowplayer(FlowplayerTestExtension);
    return true;
  }

  // The flow player entry-point did not run yet.
  return false;
};

/**
 * At this point in time, {@link FlowplayerUMD} and {@link Player} are
 * available.
 * The next step is waiting for the initialization of the flow player playlist
 * plugin.
 */
const afterFlowplayerInstancesObtained = (
  globalFlowplayer: ExtendedFlowplayerUMD,
  localFlowplayer: ExtendedPlayer,
  callback: PlaylistReadyCallback
) => {
  if (globalFlowplayer.playlist) {
    afterFlowplayerPlaylistPluginInitialization(
      globalFlowplayer,
      localFlowplayer,
      callback
    );
    return;
  }

  localFlowplayer.on("mount", () =>
    afterFlowplayerPlaylistPluginInitialization(
      globalFlowplayer,
      localFlowplayer,
      callback
    )
  );
};

/**
 * The flow player playlist plugin is now available.
 * The next step is to wait until the flow player playlist information of the
 * local player is ready.
 */
const afterFlowplayerPlaylistPluginInitialization = (
  globalFlowplayer: ExtendedFlowplayerUMD,
  localFlowplayer: ExtendedPlayer,
  callback: PlaylistReadyCallback
) => {
  if (localFlowplayer.playlist && localFlowplayer.playlist.queue) {
    callback(globalFlowplayer, localFlowplayer);
    return;
  }

  const playlistReadyEventName =
    globalFlowplayer.playlist!.events.PLAYLIST_READY;

  const playlistReadyEventHandler = () => {
    localFlowplayer.off(playlistReadyEventName, playlistReadyEventHandler);
    setTimeout(() => callback(globalFlowplayer, localFlowplayer));
  };

  localFlowplayer.on(playlistReadyEventName, playlistReadyEventHandler);
};

/**
 * The queue of the playlist plugin.
 *
 * @see https://docs.flowplayer.com/plugins/playlist#api-properties
 */
export interface FlowplayerPlaylistQueue {
  /**
   * Index of currently playing video
   */
  idx: number;
  /**
   * Videos in the playlist queue
   */
  members: {
    title: string;
    poster: string;
  }[];
  /**
   * Last possible index to play
   */
  last_idx: number;
}

/**
 * The playlist plugin.
 *
 * @see https://docs.flowplayer.com/plugins/playlist#api
 */
export interface FlowplayerPlaylistExtension {
  /**
   * Current playlist queue.
   */
  queue: FlowplayerPlaylistQueue;

  /**
   * Play video at index {@link idx}
   */
  play(idx: number): void;
}

interface ExtendedPlayer extends Player {
  playlist?: FlowplayerPlaylistExtension;
}

/**
 * @see https://docs.flowplayer.com/plugins/playlist#api-events
 */
interface FlowplayerPlaylistUMD {
  events: {
    /**
     * Fired when the playlist is updated. Gets the updated video queue as
     * parameter `queue`
     */
    PLAYLIST_READY: string;
    /**
     * Fired when the playlist advances
     */
    PLAYLIST_NEXT: string;
    /**
     * Fired when the playlist has been finished.
     */
    PLAYLIST_ENDED: string;
  };
}

interface ExtendedFlowplayerUMD extends FlowplayerUMD {
  playlist?: FlowplayerPlaylistUMD;
}

declare global {
  interface Window {
    flowplayer?: ExtendedFlowplayerUMD;
  }
}
